import { useState, useMemo, useEffect } from "react";
import { useClientsSearch } from "@/hooks/use-clients-search";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { SiteHeader } from "@/components/site-header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter,
} from "@/components/ui/sheet";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle, DialogClose,
} from "@/components/ui/dialog";
import { SearchableSelect } from "@/components/ui/searchable-select";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  SearchIcon, PlusIcon, PencilIcon, Trash2Icon, ExternalLinkIcon, RotateCcwIcon,
} from "lucide-react";
import { Pagination } from "@/components/ui/pagination";
import { authorizedFetch } from "@/lib/api";
import { usePermissions } from "@/hooks/use-permissions";
import { toast } from "sonner";

const API = import.meta.env.VITE_API_URL ?? "http://localhost:3001";
const PAGE_SIZE = 25;

interface ContractRow {
  id:                  string;
  name:                string;
  workLine:            string;
  monthlyTargetTn:     string;
  refWeightTnPerTruck: string;
  startDate:           string;
  endDate:             string | null;
  notes:               string | null;
  deletedAt:           string | null;
  createdAt:           string;
  clientId:            string;
  clientName:          string | null;
  clientCode:          string | null;
}

interface ClientOption { id: string; name: string; code: string }

interface FormState {
  clientId:            string;
  name:                string;
  monthlyTargetTn:     string;
  refWeightTnPerTruck: string;
  startDate:           string;
  endDate:             string;
  notes:               string;
}

const EMPTY_FORM: FormState = {
  clientId: "", name: "", monthlyTargetTn: "", refWeightTnPerTruck: "34",
  startDate: "", endDate: "", notes: "",
};

function formatTn(val: string | number) {
  return Number(val).toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatDate(d: string) {
  const [y, m, day] = d.split("-");
  return `${day}/${m}/${y}`;
}

export default function ContractsPage() {
  const qc = useQueryClient();
  const { can } = usePermissions();
  const canWrite = can("write", "contracts");

  const [search, setSearch]     = useState("");
  const [page, setPage]         = useState(1);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [editing, setEditing]   = useState<ContractRow | null>(null);
  const [deleting, setDeleting] = useState<ContractRow | null>(null);
  const [form, setForm]         = useState<FormState>(EMPTY_FORM);
  const [formError, setFormError] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<"active" | "deleted" | "all">("active");
  const [restoringId, setRestoringId]   = useState<string | null>(null);

  useEffect(() => { setPage(1) }, [search, filterStatus]);

  const { data: rows = [], isLoading } = useQuery<ContractRow[]>({
    queryKey: ["contracts", filterStatus],
    queryFn: async () => {
      const params = filterStatus === "all" ? "" : filterStatus === "deleted" ? "?deleted=true" : "?deleted=false";
      const res = await authorizedFetch(`${API}/api/relations/contracts${params}`);
      return res.json();
    },
  });

  const {
    clients: clientOptions,
    onSearchChange: onClientSearchChange,
  } = useClientsSearch({ enabled: sheetOpen });

  const clientSelectOptions = useMemo(() =>
    clientOptions.map(c => ({ value: c.id, label: c.name, sublabel: c.code })),
    [clientOptions]
  );

  const filtered = useMemo(() => {
    const term = search.toLowerCase().trim();
    return rows.filter(r =>
      !term ||
      r.name.toLowerCase().includes(term) ||
      (r.clientName ?? "").toLowerCase().includes(term) ||
      (r.clientCode ?? "").toLowerCase().includes(term)
    );
  }, [rows, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paged      = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  function openCreate() {
    setEditing(null);
    setForm(EMPTY_FORM);
    setFormError(null);
    setSheetOpen(true);
  }

  function openEdit(row: ContractRow) {
    setEditing(row);
    setForm({
      clientId:            row.clientId,
      name:                row.name,
      monthlyTargetTn:     row.monthlyTargetTn,
      refWeightTnPerTruck: row.refWeightTnPerTruck,
      startDate:           row.startDate,
      endDate:             row.endDate ?? "",
      notes:               row.notes ?? "",
    });
    setFormError(null);
    setSheetOpen(true);
  }

  const saveMut = useMutation({
    mutationFn: async () => {
      const body = {
        clientId:            form.clientId,
        name:                form.name,
        monthlyTargetTn:     Number(form.monthlyTargetTn),
        refWeightTnPerTruck: Number(form.refWeightTnPerTruck),
        startDate:           form.startDate,
        endDate:             form.endDate || null,
        notes:               form.notes || null,
      };
      if (editing) {
        await authorizedFetch(`${API}/api/relations/contracts/${editing.id}`, {
          method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
        });
      } else {
        await authorizedFetch(`${API}/api/relations/contracts`, {
          method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
        });
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["contracts"] });
      setSheetOpen(false);
      setEditing(null);
      setFormError(null);
      toast.success(editing ? "Contrato actualizado" : "Contrato creado");
    },
    onError: (err: Error) => setFormError(err.message),
  });

  const deleteMut = useMutation({
    mutationFn: async () => {
      await authorizedFetch(`${API}/api/relations/contracts/${deleting!.id}`, { method: "DELETE" });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["contracts"] });
      setDeleteOpen(false);
      setDeleting(null);
      toast.success("Contrato eliminado");
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const restoreMut = useMutation({
    mutationFn: async (id: string) => {
      setRestoringId(id);
      await authorizedFetch(`${API}/api/relations/contracts/${id}/restore`, { method: "PATCH" });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["contracts"] });
      toast.success("Contrato restaurado");
    },
    onError: (err: Error) => toast.error(err.message),
    onSettled: () => setRestoringId(null),
  });

  const isSaving = saveMut.isPending;

  return (
    <>
      <SiteHeader title="Contratos" />
      <div className="flex flex-1 flex-col px-4 py-6 lg:px-6">
        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <div className="relative flex-1 min-w-48">
            <SearchIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por nombre o cliente..."
              className="pl-8"
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
            />
          </div>
          <Select value={filterStatus} onValueChange={v => setFilterStatus(v as "active" | "deleted" | "all")}>
            <SelectTrigger className="w-36">
              <SelectValue>
                {filterStatus === "active" ? "Activos" : filterStatus === "deleted" ? "Eliminados" : "Todos"}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Activos</SelectItem>
              <SelectItem value="deleted">Eliminados</SelectItem>
              <SelectItem value="all">Todos</SelectItem>
            </SelectContent>
          </Select>
          {canWrite && filterStatus !== "deleted" && (
            <Button onClick={openCreate}>
              <PlusIcon className="size-4 mr-1" />
              Nuevo contrato
            </Button>
          )}
        </div>

        {/* Table */}
        <div className="rounded-md border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nombre</TableHead>
                <TableHead>Cliente</TableHead>
                <TableHead>Línea</TableHead>
                <TableHead className="text-right">Objetivo mens. (TN)</TableHead>
                <TableHead className="text-right">Ref/camión (TN)</TableHead>
                <TableHead>Período</TableHead>
                <TableHead className="w-24" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">Cargando...</TableCell></TableRow>
              ) : paged.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">Sin contratos</TableCell></TableRow>
              ) : paged.map(row => (
                <TableRow key={row.id} className={row.deletedAt ? "opacity-50" : undefined}>
                  <TableCell className="font-medium">
                    <Link to={`/relations/contracts/${row.id}`} className="hover:underline text-primary">
                      {row.name}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <span className="text-muted-foreground text-xs font-mono mr-1">{row.clientCode}</span>
                    {row.clientName}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="capitalize">{row.workLine}</Badge>
                  </TableCell>
                  <TableCell className="text-right font-mono">{formatTn(row.monthlyTargetTn)}</TableCell>
                  <TableCell className="text-right font-mono">{formatTn(row.refWeightTnPerTruck)}</TableCell>
                  <TableCell className="text-sm">
                    {formatDate(row.startDate)}
                    {row.endDate ? ` → ${formatDate(row.endDate)}` : " →"}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1 justify-end">
                      {!row.deletedAt && (
                        <Link to={`/relations/contracts/${row.id}`}>
                          <Button variant="ghost" size="icon">
                            <ExternalLinkIcon className="size-4" />
                          </Button>
                        </Link>
                      )}
                      {canWrite && !row.deletedAt && (
                        <>
                          <Button variant="ghost" size="icon" onClick={() => openEdit(row)}>
                            <PencilIcon className="size-4" />
                          </Button>
                          <Button variant="ghost" size="icon" className="text-destructive" onClick={() => { setDeleting(row); setDeleteOpen(true); }}>
                            <Trash2Icon className="size-4" />
                          </Button>
                        </>
                      )}
                      {canWrite && row.deletedAt && (
                        <Button
                          variant="ghost" size="icon" className="text-green-600"
                          disabled={restoringId === row.id}
                          onClick={() => restoreMut.mutate(row.id)}
                        >
                          <RotateCcwIcon className="size-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <div className="mt-3">
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            totalItems={filtered.length}
            pageSize={PAGE_SIZE}
            onPageChange={setPage}
          />
        </div>
      </div>

      {/* Create / Edit sheet */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent side="right" className="sm:max-w-md flex flex-col">
          <SheetHeader>
            <SheetTitle>{editing ? "Editar contrato" : "Nuevo contrato"}</SheetTitle>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
            <div className="space-y-1.5">
              <Label>Cliente <span className="text-destructive">*</span></Label>
              <SearchableSelect
                options={clientSelectOptions}
                value={form.clientId}
                onValueChange={v => setForm(f => ({ ...f, clientId: v }))}
                onSearchChange={onClientSearchChange}
                placeholder="Seleccionar cliente"
                searchPlaceholder="Buscar cliente..."
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="c-name">Nombre del contrato <span className="text-destructive">*</span></Label>
              <Input id="c-name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Ej: Contrato YPF Batea Jun 2026" maxLength={200} required />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="c-target">Objetivo mensual (TN) <span className="text-destructive">*</span></Label>
                <Input id="c-target" type="number" min="0" step="0.01" value={form.monthlyTargetTn}
                  onChange={e => setForm(f => ({ ...f, monthlyTargetTn: e.target.value }))} required />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="c-ref">Ref. TN/camión <span className="text-destructive">*</span></Label>
                <Input id="c-ref" type="number" min="0" step="0.01" value={form.refWeightTnPerTruck}
                  onChange={e => setForm(f => ({ ...f, refWeightTnPerTruck: e.target.value }))} required />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="c-start">Fecha inicio <span className="text-destructive">*</span></Label>
                <Input id="c-start" type="date" value={form.startDate}
                  onChange={e => setForm(f => ({ ...f, startDate: e.target.value }))} required />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="c-end">Fecha fin</Label>
                <Input id="c-end" type="date" value={form.endDate}
                  onChange={e => setForm(f => ({ ...f, endDate: e.target.value }))} />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="c-notes">Notas</Label>
              <Input id="c-notes" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Observaciones opcionales" />
            </div>

            {formError && <p className="text-sm text-destructive">{formError}</p>}
          </div>
          <SheetFooter>
            <Button variant="outline" onClick={() => setSheetOpen(false)} disabled={isSaving}>Cancelar</Button>
            <Button onClick={() => saveMut.mutate()} disabled={isSaving || !form.clientId || !form.name || !form.monthlyTargetTn || !form.startDate}>
              {isSaving ? "Guardando..." : "Guardar"}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Delete dialog */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Eliminar contrato</DialogTitle>
            <DialogDescription>
              ¿Eliminar <strong>{deleting?.name}</strong>? Esta acción puede revertirse.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose><Button variant="outline">Cancelar</Button></DialogClose>
            <Button variant="destructive" onClick={() => deleteMut.mutate()} disabled={deleteMut.isPending}>
              {deleteMut.isPending ? "Eliminando..." : "Eliminar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
